import React, { useState } from "react";
import { StatCard, Expandable, SubExpandable, SectionTitle, LastSynced, EmptyState } from "./DashboardPrimitives";
import { DataTable } from "../../common/DataTable";

const MongodbDashboard = ({ data, formatLastConnected }) => {
  const [openDb, setOpenDb] = useState(null);
  const [openCollection, setOpenCollection] = useState(null);

  if (!data?.connector_data) return <EmptyState message="No MongoDB data available" />;

  const { scan_mode, databases } = data.connector_data;
  const totalCollections = databases.reduce((sum, db) => sum + db.collections.length, 0);
  const totalDocuments = databases.reduce((sum, db) => sum + db.collections.reduce((cSum, c) => cSum + c.document_count, 0), 0);

  // Field names and types only: the connector does not copy documents out
  // of the customer's database.
  const FieldTable = ({ fields }) => {
    if (!fields || fields.length === 0) return <p className="text-sm text-gray-400">No field information available. Sync the connector to refresh it.</p>;
    return (
      <DataTable
        columns={[
          {
            accessorKey: "name",
            header: "Field",
            cell: ({ getValue }) => <span className="font-mono">{getValue()}</span>,
          },
          {
            id: "types",
            header: "Type",
            cell: ({ row }) => <span>{(row.original.types || []).join(", ")}</span>,
          },
        ]}
        data={fields}
        getRowId={(field) => field.name}
        enableColumnVisibility={false}
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Identity strip */}
      <div className="pb-4 border-b border-gray-100">
        <p className="text-sm font-semibold text-gray-800">Scan Mode: <span className="font-normal text-gray-500">{scan_mode}</span></p>
        <LastSynced value={data?.last_synced_at} formatFn={formatLastConnected} />
      </div>

      {/* Snapshot */}
      <div>
        <SectionTitle title="Scan Snapshot" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Databases" value={databases.length} />
          <StatCard label="Collections" value={totalCollections} />
          <StatCard label="Documents" value={totalDocuments} />
          <StatCard label="Scan Mode" value={scan_mode} />
        </div>
      </div>

      {/* Databases */}
      <div className="space-y-3">
        <SectionTitle title="Databases" count={databases.length} />
        {databases.map((db) => (
          <Expandable
            key={db.database}
            title={db.database}
            isOpen={openDb === db.database}
            onToggle={() => setOpenDb(openDb === db.database ? null : db.database)}
            badge={<span className="text-xs text-gray-400">{db.collections.length} collection{db.collections.length !== 1 ? "s" : ""}</span>}
          >
            <div className="space-y-3">
              {db.collections.map((col) => (
                <SubExpandable
                  key={col.collection}
                  title={
                    <div className="flex items-center justify-between w-full pr-2">
                      <span className="font-medium text-gray-800">{col.collection}</span>
                      <span className="text-xs text-gray-400">{col.document_count} docs</span>
                    </div>
                  }
                  isOpen={openCollection === col.collection}
                  onToggle={() => setOpenCollection(openCollection === col.collection ? null : col.collection)}
                >
                  <FieldTable fields={col.fields} />
                </SubExpandable>
              ))}
            </div>
          </Expandable>
        ))}
      </div>
    </div>
  );
};

export default MongodbDashboard;
